import React, { useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import ExpenseChart from './ExpenseChart';
import {
    getDailyExpenses,
    getWeeklyExpenses,
    getMonthlyExpenses,
    getWeekendVsWeekdayExpenses,
    getSpendByDayOfWeek,
    getTopExpenses,
    getMonthStatistics
} from '../selectors/analytics';

export const AnalyticsPage = ({ expenses, history }) => {
    const [viewMode, setViewMode] = useState('daily');
    const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));
    const selectedMonthMoment = moment(selectedMonth, 'YYYY-MM');

    // Dynamically retrieve all unique months contained within database 
    const availableMonths = [...new Set(expenses.map(e => moment(e.createdAt).format('YYYY-MM')))];
    
    if (!availableMonths.includes(moment().format('YYYY-MM'))) {
        availableMonths.push(moment().format('YYYY-MM'));
    }
    availableMonths.sort((a, b) => b.localeCompare(a)); // Descending chronological

    // Compute universal Month Statistics
    const stats = getMonthStatistics(expenses, selectedMonthMoment);

    // Handler for clicking exactly on a Bar in the chart
    const onChartBarClick = (payload) => {
        if (payload && payload.id) {
            history.push(`/edit/${payload.id}`);
        }
    };

    let data = [];
    let xAxisKey = 'name';
    let barDataKey = 'amount';
    let dataKeyLabel = 'Spend';

    switch (viewMode) {
        case 'daily':
            data = getDailyExpenses(expenses, selectedMonthMoment);
            break;
        case 'weekly':
            data = getWeeklyExpenses(expenses, selectedMonthMoment);
            break;
        case 'weekend':
            data = getWeekendVsWeekdayExpenses(expenses, selectedMonthMoment);
            break;
        case 'monthly':
            data = getMonthlyExpenses(expenses, selectedMonthMoment);
            break;
        case 'dayofweek':
            data = getSpendByDayOfWeek(expenses, selectedMonthMoment);
            break;
        case 'topexpenses':
            data = getTopExpenses(expenses, selectedMonthMoment);
            break;
        default:
            data = getDailyExpenses(expenses, selectedMonthMoment);
    }

    const setMode = (mode) => () => {
        setViewMode(mode);
    };

    return (
        <div className="analytics-page container">
            <div className="analytics-header">
                <h2 className="analytics-header__title">Expense Analytics</h2>
                <div style={{ marginTop: '1.5rem' }}>
                    <select 
                        value={selectedMonth} 
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        style={{ padding: '0.6rem 1rem', fontSize: '1.4rem', borderRadius: '4px', border: '1px solid #ccc', outline: 'none' }}
                    >
                        {availableMonths.map(month => (
                            <option key={month} value={month}>
                                {moment(month, 'YYYY-MM').format('MMMM YYYY')}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="analytics-controls">
                <button 
                    className={`analytics-button ${viewMode === 'daily' ? 'active' : ''}`} 
                    onClick={setMode('daily')}
                >
                    Daily
                </button>
                <button 
                    className={`analytics-button ${viewMode === 'weekly' ? 'active' : ''}`} 
                    onClick={setMode('weekly')}
                >
                    Weekly
                </button>
                <button 
                    className={`analytics-button ${viewMode === 'weekend' ? 'active' : ''}`} 
                    onClick={setMode('weekend')}
                >
                    Weekends vs Weekdays
                </button>
                <button 
                    className={`analytics-button ${viewMode === 'dayofweek' ? 'active' : ''}`} 
                    onClick={setMode('dayofweek')}
                >
                    Day of Week
                </button>
                <button 
                    className={`analytics-button ${viewMode === 'monthly' ? 'active' : ''}`} 
                    onClick={setMode('monthly')}
                >
                    Monthly (12 Mo)
                </button>
                <button 
                    className={`analytics-button ${viewMode === 'topexpenses' ? 'active' : ''}`} 
                    onClick={setMode('topexpenses')}
                >
                    Top Expenses
                </button>
            </div>

            <div className="analytics-chart-container">
                <h3 className="analytics-chart-title">
                    {viewMode === 'daily' && `Daily Spend Trends (${selectedMonthMoment.format('MMMM YYYY')})`}
                    {viewMode === 'weekly' && `Weekly Spend Trends (${selectedMonthMoment.format('MMMM YYYY')})`}
                    {viewMode === 'weekend' && `Weekday vs Weekend Total (${selectedMonthMoment.format('MMMM YYYY')})`}
                    {viewMode === 'dayofweek' && `Total Spend by Day of Week (${selectedMonthMoment.format('MMMM YYYY')})`}
                    {viewMode === 'monthly' && `Monthly Spend Trends (Trailing 12 Months from ${selectedMonthMoment.format('MMM YYYY')})`}
                    {viewMode === 'topexpenses' && `Top 5 Purchases (${selectedMonthMoment.format('MMMM YYYY')})`}
                </h3>
                
                {data.length > 0 ? (
                    <ExpenseChart 
                        data={data} 
                        xAxisKey={xAxisKey} 
                        barDataKey={barDataKey} 
                        dataKeyLabel={dataKeyLabel} 
                        onBarClick={onChartBarClick}
                    />
                ) : (
                    <p style={{textAlign: 'center', marginTop: '2rem'}}>No expense data available to build charts.</p>
                )}
            </div>

            {/* Statistics Summary Section */}
            <div className="analytics-summary">
                <div className="analytics-summary-card">
                    <span className="stat-label">Total Spends</span>
                    <span className="stat-value">Rs {stats.total}</span>
                </div>
                <div className="analytics-summary-card">
                    <span className="stat-label">Daily Average</span>
                    <span className="stat-value">Rs {stats.averageDaily}</span>
                </div>
                <div className="analytics-summary-card">
                    <span className="stat-label">Largest Purchase</span>
                    <span className="stat-value">Rs {stats.highest}</span>
                </div>
                <div className="analytics-summary-card">
                    <span className="stat-label">Peak Spend Day</span>
                    <span className="stat-value">{stats.busiestDay}</span>
                </div>
                <div className="analytics-summary-card">
                    <span className="stat-label">Total Purchases</span>
                    <span className="stat-value">{stats.count}</span>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    expenses: state.expenses
});

export default connect(mapStateToProps)(AnalyticsPage);
